import React, { useCallback, useMemo, useState } from 'react'
import withFormContext, {
  PropsWithFormContext,
} from 'core/components/validatedForm/withFormContext'
import Input, { InputProps } from 'core/elements/input/Input'

interface TextFieldProps extends InputProps {
  returnAsString?: boolean
  type?: string
  integer?: boolean
}

const TextField = ({
  value,
  label,
  id,
  hasError,
  errorMessage,
  required,
  variant,
  onChange,
  type,
  integer,
  returnAsString,
  getCurrentValue,
  updateFieldValue,
  setFieldValue,
  startAdornment,
  endAdornment,
  ...restProps
}: PropsWithFormContext<string, TextFieldProps>) => {
  const isNumber = useMemo(() => type?.toLowerCase() === 'number', [type])
  const isPassword = useMemo(() => type?.toLowerCase() === 'password', [type])
  const [showPassword, setShowPassword] = useState(false)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // HTML specs says that <input type="number"> return strings but it's more useful if we
      // convert it to a `Number` to reduce type casting all over the place.
      const strVal = e.target.value

      if (strVal === '') {
        onChange?.('')
        return
      }

      const value = isNumber ? Number(strVal) : strVal
      const _value = integer ? Math.floor(Number(value)) : value
      const returnValue = returnAsString ? String(_value) : isNumber ? Number(_value) : _value

      onChange?.(returnValue)
    },
    [onChange, isNumber, integer, returnAsString],
  )

  return (
    <Input
      {...restProps}
      label={label && required ? `${label} *` : label}
      variant={variant}
      error={errorMessage}
      value={[undefined, null, ''].includes(value) ? '' : isNumber ? Number(value) : value}
      onChange={handleChange}
      type={isPassword ? (showPassword ? 'text' : 'password') : type}
      data-testid={`input--${id || label?.replace(/\s+/g, '-')}`}
      id={`${id}-field`}
      style={{ scrollMarginTop: '40px' }}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      {...(isPassword && {
        icon: showPassword ? 'eye' : 'eye-slash',
        iconProps: { placement: 'end', onClick: () => setShowPassword((prev) => !prev) },
      })}
    />
  )
}

export default withFormContext(TextField)
